import type { IEffectsPreviewStyle, ILoopingAnimationItem } from "@kuika/kuika-cl-model";
import { StyleHelper } from "@kuika/kuika-cl-model";
import React from "react";
import KuikaSmallText from "../../../../ui-kit/typography/SmallText/SmallText";
import {
  buildLoopAnimationOptions,
  buildLoopKeyframes,
  buildPreviewEffects,
  buildRevealKeyframes,
  buildSampleStyle,
  getPreviewSampleSpec,
  REVEAL_PREVIEW_OPTIONS
} from "./effects-presets-preview";
import type { CssVarStyle, IPreviewSampleSpec } from "./effects-presets-preview";
import type { EffectPreset } from "./effects-presets-types";

export interface EffectsPresetCardProps {
  preset: EffectPreset;
  onApply: (preset: EffectPreset) => void;
  dataTestId?: string;
  /** Position in the visible grid — drives the staggered entrance delay. */
  enterIndex?: number;
}

const ENTER_STAGGER_MS: number = 22;
const ENTER_STAGGER_CAP: number = 12;
const MAX_VISIBLE_TAGS: number = 3;

/**
 * The mini component on the stage. All shapes render as a div (the stage is
 * aria-hidden, so there is no semantic loss) — that keeps the WAAPI ref a
 * single HTMLDivElement across kinds.
 */
const renderSample = (
  spec: IPreviewSampleSpec,
  sampleStyle: CssVarStyle,
  sampleRef: React.RefObject<HTMLDivElement>
): React.ReactNode => {
  const base: string = "effects-presets-popover__sample";
  const className: string = `${base} ${base}--${spec.sampleKind}`;
  if (spec.sampleKind === "card") {
    return (
      <div ref={sampleRef} className={className} style={sampleStyle} data-trigger={spec.dataTrigger}>
        <span className="effects-presets-popover__sample-title">{spec.label}</span>
        <span className="effects-presets-popover__sample-line" />
        <span className="effects-presets-popover__sample-line effects-presets-popover__sample-line--short" />
      </div>
    );
  }
  return (
    <div ref={sampleRef} className={className} style={sampleStyle} data-trigger={spec.dataTrigger}>
      {spec.label}
    </div>
  );
};

/**
 * One preset card: a labeled mini component on a stage + a badge teaching WHEN
 * the effect fires. The preview is derived from the preset's REAL payload:
 * interaction presets publish `--fx-*` vars consumed by the SCSS
 * `.card:hover/:active` trigger rules; static presets render their resting
 * look inline; motion / reveal presets loop via WAAPI.
 */
const EffectsPresetCard: React.FC<EffectsPresetCardProps> = ({ preset, onApply, dataTestId, enterIndex }) => {
  const spec: IPreviewSampleSpec = getPreviewSampleSpec(preset);
  const previewStyle: IEffectsPreviewStyle = StyleHelper.buildEffectsPreviewStyle(buildPreviewEffects(preset));
  const sampleStyle: CssVarStyle = buildSampleStyle(previewStyle, spec.triggerKind);
  const sampleRef: React.RefObject<HTMLDivElement> = React.useRef<HTMLDivElement>(null);

  React.useEffect((): (() => void) | undefined => {
    if (spec.triggerKind !== "loop") {
      return undefined;
    }
    const el: HTMLDivElement | null = sampleRef.current;
    if (!el || typeof el.animate !== "function") {
      return undefined;
    }
    const reducedMotion: boolean = window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false;
    if (reducedMotion) {
      return undefined;
    }
    let keyframes: Keyframe[];
    let options: KeyframeAnimationOptions;
    if (spec.loopSource === "reveal" && preset.initialStateHint) {
      keyframes = buildRevealKeyframes(preset.initialStateHint, preset.entryTiming?.timingFunction);
      options = REVEAL_PREVIEW_OPTIONS;
    } else if (preset.payload.animations && preset.payload.animations.length > 0) {
      const item: ILoopingAnimationItem = preset.payload.animations[0];
      keyframes = buildLoopKeyframes(item);
      options = buildLoopAnimationOptions(item);
    } else {
      return undefined;
    }
    if (keyframes.length === 0) {
      return undefined;
    }
    const animation: Animation = el.animate(keyframes, options);
    return (): void => {
      animation.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- spec/keyframes derive from the preset; id is the identity
  }, [preset.id]);

  const enterDelayMs: number = Math.min(enterIndex ?? 0, ENTER_STAGGER_CAP) * ENTER_STAGGER_MS;
  const cardStyle: CssVarStyle = { "--enter-delay": `${enterDelayMs}ms` };
  const visibleTags: string[] = preset.tags.slice(0, MAX_VISIBLE_TAGS);

  const handleClick = (): void => {
    onApply(preset);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onApply(preset);
    }
  };

  return (
    <div
      className="effects-presets-popover__card"
      style={cardStyle}
      role="button"
      tabIndex={0}
      aria-label={`Apply ${preset.name} preset (${spec.badge.toLowerCase()})`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      data-testid={dataTestId}
      data-preset-id={preset.id}
    >
      <div
        className={`effects-presets-popover__preview effects-presets-popover__preview--kind-${spec.sampleKind}`}
        aria-hidden="true"
      >
        <span className={`effects-presets-popover__badge effects-presets-popover__badge--${spec.badgeTone}`}>
          {spec.badge}
        </span>
        {renderSample(spec, sampleStyle, sampleRef)}
      </div>
      <div className="effects-presets-popover__card-body">
        <KuikaSmallText style={{ color: "var(--dark-11)", fontWeight: 700, fontSize: 13 }}>
          {preset.name}
        </KuikaSmallText>
        <KuikaSmallText style={{ color: "var(--dark-5)", fontSize: 11.5, lineHeight: 1.35 }}>
          {preset.description}
        </KuikaSmallText>
        {visibleTags.length > 0 && (
          <div className="effects-presets-popover__card-tags">
            {visibleTags.map((tag: string): React.ReactNode => (
              <span key={tag} className="effects-presets-popover__card-tag">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EffectsPresetCard;
